import {
  DEFAULT_PDL_DAILY_RATE,
  PERIOD_MAPPING,
  PDL_MIN_LOAN_AMOUNT,
  INSTALLMENT_MIN_LOAN_AMOUNT,
  INSTALLMENT_MAX_LOAN_AMOUNT,
} from "./constants";

/**
 * Класс утилит для калькулятора кредитов
 */
export default class CalcUtilities {
  /**
   * Форматирует число с пробелами (разделитель тысяч)
   * @param {number} value - Число для форматирования
   * @returns {string} Отформатированная строка
   */
  static formatNumber(value) {
    return value.toLocaleString("ru-RU");
  }

  /**
   * Добавляет ручке noUiSlider имя и актуальные aria-value*.
   * Библиотека ставит role="slider" на ручку, но значения пишет во вложенный
   * touch-area, поэтому скринридер не видит ни название, ни значение.
   * @param {HTMLElement} sliderElement - Элемент с инициализированным noUiSlider
   * @param {string} label - Название ползунка для скринридера
   */
  static bindSliderAria(sliderElement, label) {
    const slider = sliderElement?.noUiSlider;
    const handle = sliderElement?.querySelector(".noUi-handle");

    if (!slider || !handle) {
      return;
    }

    handle.setAttribute("aria-label", label);

    // Событие update вызывается сразу при подписке и при каждом изменении
    slider.on("update", () => {
      const { min, max } = slider.options.range;
      const value = parseInt(slider.get(), 10);

      handle.setAttribute("aria-valuemin", min);
      handle.setAttribute("aria-valuemax", max);
      handle.setAttribute("aria-valuenow", value);
      handle.setAttribute(
        "aria-valuetext",
        `${CalcUtilities.formatNumber(value)} ₸`,
      );
    });
  }

  /**
   * Форматирует денежное значение с округлением до 2 знаков и пробелами
   * @param {number} value - Число для форматирования
   * @returns {string} Отформатированная строка
   */
  static formatMoney(value) {
    const rounded = Math.round(value * 100) / 100;
    return rounded.toLocaleString("ru-RU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  /**
   * Форматирует проценты (целая часть или с 2 знаками после запятой)
   * @param {number} value - Значение в долях (например, 0.3796)
   * @returns {string} Отформатированная строка процентов
   */
  static formatPercent(value) {
    const percent = value * 100;
    return percent.toFixed(2).replace(".", ",");
  }

  /**
   * Получает текущую дату в формате YYYY-MM-DD
   * @returns {string} Дата в формате YYYY-MM-DD
   */
  static getCurrentDate() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  /**
   * Проверяет, является ли период PDL (дневным)
   * @param {string} periodValue - Значение периода
   * @returns {boolean} true если PDL, false если Installment
   */
  static isPdlPeriod(periodValue) {
    const period = PERIOD_MAPPING[periodValue];
    return period && period.isDays;
  }

  /**
   * Получает диапазон сумм кредита в зависимости от типа периода
   * @param {string} periodValue - Значение периода
   * @returns {Object} Объект с min и max суммами кредита
   */
  static getLoanRangeByPeriod(periodValue) {
    const periodData = PERIOD_MAPPING[periodValue];
    if (!periodData) {
      // Fallback для неизвестных периодов
      return {
        min: INSTALLMENT_MIN_LOAN_AMOUNT,
        max: INSTALLMENT_MAX_LOAN_AMOUNT,
      };
    }

    const isPdl = periodData.isDays;
    if (isPdl) {
      // Для PDL используем maxAmount из маппинга периода
      return {
        min: PDL_MIN_LOAN_AMOUNT,
        max: periodData.maxAmount || 300_000,
      };
    } else {
      // Для INSTALLMENT фиксированные лимиты
      return {
        min: INSTALLMENT_MIN_LOAN_AMOUNT,
        max: INSTALLMENT_MAX_LOAN_AMOUNT,
      };
    }
  }

  /**
   * Получает дневную ставку для PDL в зависимости от срока
   * Базируется на годовой ставке 37,96%
   * @returns {number} Дневная ставка в долях единицы
   */
  static getPdlDailyRate() {
    return DEFAULT_PDL_DAILY_RATE;
  }
}
